import { WidgetsByStandard } from '@atlas-metrics/microsite-types/dist/types/typeIndex';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { getS3HeaderUrl } from '@/utils/getS3HeaderUrl';
import { transformWidgetToSubNavigationData } from '@/utils/transformWidgetToSubNavigationData';

import CompanyHeader from './layout/CompanyHeader';
import NoData from './NoData';
import { OrganizationSubNavigation } from './OrganizationSubNavigation';
import { OrganizationTabsFilter } from './OrganizationTabsFilter';
import ProjectsContent from './ProjectsContent';
import StandardContent from './StandardContent';
import StandardTabs from './StandardTabs';
import FloatingBar from './ui/FloatingBar';
import Icon from './ui/Icon';
import { Content } from './ui/layout';

import { Organization, Projects } from '@/types';

export default function OrganizationComponent({
  organization,
  widgets,
  switchReportPeriod,
  loadingYear,
  projects,
}: {
  widgets?: WidgetsByStandard[] | null;
  organization: Organization;
  switchReportPeriod: (period?: string) => void;
  loadingYear: string | null;
  projects: Projects | null;
}) {
  const standardsTabsData = useMemo(
    () =>
      organization.standards?.map((standard) => ({
        // TODO: has to be solved in logic model by ESG team
        label:
          standard.id === 'ATM' && organization.language === 'de_DE'
            ? 'Alle'
            : standard.tag,
        value: standard.id,
      })),
    [organization.standards, organization.language]
  );

  const [selectedStandard, setSelectedStandard] = useState(
    standardsTabsData?.[0]?.value
  );

  useEffect(() => {
    setSelectedStandard(standardsTabsData?.[0]?.value);
  }, [standardsTabsData]);

  const widgetsByStandard = (widgets ?? []).filter(
    (widget) => widget.id === selectedStandard
  );

  const subNavigationData =
    transformWidgetToSubNavigationData(widgetsByStandard);

  const [themesInView, setThemesInView] = useState<string[]>([]);
  const [targetTheme, setTargetTheme] = useState<string>('');

  const { ref, inView: isFloatingBarInvisible } = useInView();
  const { ref: projectRef, inView: isProjectSectionVisible } = useInView();
  const { ref: themeRef } = useInView({
    triggerOnce: true,
    rootMargin: '0px 0px -80px 0px',
  });

  const themeNodeRef = useRef<{
    [key: string]: HTMLElement;
  }>({});

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setTargetTheme('');
    }, 1000);
    return () => {
      clearTimeout(timeOut);
    };
  }, [targetTheme]);

  const onThemeViewChange = (themeId: string, inView: boolean) => {
    setThemesInView((prevItem) => {
      // this only happens when the user clicks on the sub-navigation
      if (targetTheme) {
        if (inView) {
          return [...prevItem, themeId, targetTheme];
        }
        return [...prevItem, targetTheme];
      }

      if (inView) {
        return [...prevItem, themeId];
      }

      return prevItem.filter((name) => name !== themeId);
    });
  };

  const selectedSubNavigationItem = themesInView[themesInView.length - 1];

  return (
    <main className='relative min-h-[calc(100vh+70px)]'>
      <CompanyHeader
        companyName={organization.name}
        companyDescription={organization.description}
        backgroundColor={organization.color}
        largeHeaderUrl={getS3HeaderUrl(organization?.header?.large)}
        smallHeaderUrl={getS3HeaderUrl(organization?.header?.small)}
        avatar={
          organization.logoUrl ? (
            <Image
              src={organization.logoUrl}
              alt={organization.name}
              fill
              className='rounded-full'
            />
          ) : (
            <Icon
              height={27}
              width={30}
              name='icon-company-placeholder'
              className='text-ds-grey-950'
            />
          )
        }
      />
      <div className='left-0 top-0 z-10 webM:sticky'>
        <OrganizationTabsFilter
          selectedStandard={selectedStandard}
          setSelectedStandard={setSelectedStandard}
          selectedPeriod={organization.selectedReportPeriod}
          setSelectedPeriod={(id) =>
            switchReportPeriod(
              organization.reportPeriods.find((period) => period.id === id)
                ?.name ?? undefined
            )
          }
          standardsTabsData={standardsTabsData}
          reportingPeriodsTabsData={organization.reportPeriods
            .sort((period1, period2) =>
              period2.name.localeCompare(period1.name)
            )
            .map((period) => ({
              label: period.name,
              value: period.id,
              ...(period.name === loadingYear && { loading: true }),
            }))}
        />
      </div>
      <Content centered type='body'>
        <div className='mb-20 pb-4 webL:mb-0'>
          {widgetsByStandard == null || widgetsByStandard.length === 0 ? (
            <NoData />
          ) : (
            <div className='flex items-start gap-20'>
              <OrganizationSubNavigation
                subNavigationData={subNavigationData}
                selected={selectedSubNavigationItem}
                setTargetTheme={setTargetTheme}
                themeNodeRef={themeNodeRef}
                fontColor={organization.fontColor}
              />
              <div className='flex-grow'>
                {widgetsByStandard.map((standard) => (
                  <div className='mt-5' key={standard.id}>
                    <StandardContent
                      standard={standard}
                      themeRef={themeRef}
                      themeNodeRef={themeNodeRef}
                      onThemeViewChange={onThemeViewChange}
                      fontColor={organization.fontColor}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {projects && projects.project && projects.entries?.length && (
          <div ref={projectRef}>
            <ProjectsContent projects={projects} />
          </div>
        )}
      </Content>
      <div
        ref={ref}
        className='relative z-[100] transition-opacity webM:invisible'
      >
        {!isFloatingBarInvisible &&
          !isProjectSectionVisible &&
          standardsTabsData.length > 1 && (
            <FloatingBar className='w-screen'>
              <StandardTabs
                selectedStandard={selectedStandard}
                setSelectedStandard={setSelectedStandard}
                standardsTabsData={standardsTabsData}
                className='overflow-scroll rounded-2xl bg-ds-grey-50 pr-2'
              />
            </FloatingBar>
          )}
      </div>
    </main>
  );
}
